/**
 * genreTaxonomy.js — shared with reanlo-author (byte-for-byte port, PR3,
 * docs/genre-subgenre-build-spec.md). This matching logic has no
 * reader-vs-author mode -- both Control A (this repo's write-in surfaces)
 * and the author form call the exact same findSubgenreMatch/buildMatchIndex,
 * so a write-in behaves identically wherever it is typed. Keep this file in
 * sync with reanlo-author/src/lib/genreTaxonomy.js by hand until the two
 * repos share a package; if you change one, change both and say so in the PR.
 *
 * PR2's data + matching layer for the genre/subgenre picker
 * (genre-subgenre-build-spec.md, Part 5). Kept import-free of Supabase and
 * React on purpose, same reasoning as src/lib/tagRows.js: the matching logic
 * is a pure function of (typed text, chosen genre, the subgenre list), so it
 * can be tested under plain node with no client/stub, and no test can quietly
 * pass against a mock that behaves differently from the real thing.
 *
 * WHY THIS IS NOT process-trope REUSED, EVEN THOUGH THE SPEC ASKS FOR THAT.
 *
 * Part 5 says to "reuse the comparison that already exists for tropes...
 * Confirm before estimating: that service both compares and writes a tropes
 * row, so the comparison may need separating from the write to be usable
 * against a different table." That IS the honest answer, not a suggestion to
 * ignore: process-trope's thresholds (tropeUtils.js — alias/exact, >=0.90
 * auto-merge, 0.75-0.89 review, <0.75 new) are OpenAI embedding similarities,
 * a genuinely semantic comparison. Reusing it for real means splitting an
 * edge function that currently both compares AND writes into a
 * comparison-only path callable against `subgenres` instead of `tropes` --
 * that is backend work, not a client-side matcher, and it was flagged rather
 * than built here for the same reason the book_tags dual-write question was
 * flagged before this file was written: it changes what gets deployed and
 * deserves a yes/no, not a silent guess.
 *
 * What follows instead is a considered, tested INTERIM matcher, run against
 * every one of the ten cases the spec's own audit used to fail the starter
 * code (see the table in Part 5, "The starter code's matcher was run against
 * real inputs"). It fixes the two defects that are pure bugs (only searching
 * the selected genre's list; the bare substring test producing false
 * positives) without claiming to be a semantic matcher. It will still miss
 * things a real embedding comparison would catch -- that gap is real and
 * belongs to the flagged decision above, not to a bug in this file.
 */

/**
 * Mirrors public.normalize_subgenre_name(text) EXACTLY (PR1's migration,
 * section 1): lowercase, replace only . & - with a space, collapse
 * whitespace, trim. Kept byte-for-byte identical so a name that normalizes
 * to the same string here normalizes to the same string in the
 * UNIQUE (genre_id, normalized_name) constraint that actually enforces
 * "one row forever" (Part 5, decision 9) -- this file's normalization is a
 * convenience for matching, the database's is the guarantee.
 */
export function normalizeSubgenreName(raw) {
  return (raw || '')
    .toLowerCase()
    .replace(/[.&-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The names to show under a book's "Genre" heading: its book_genres names,
 * then its book_subgenres names, in the order the rows were written, with
 * duplicates dropped.
 *
 * Takes a books row fetched with
 *   book_genres ( id, genre:genres ( name ) ),
 *   book_subgenres ( id, subgenre:subgenres ( name ) )
 * embedded -- both tables are publicly readable (PR 1's migration), so this
 * works for a signed-out visitor too.
 *
 * Reads the new tables first. Since PR 5
 * (20260926000000_pr5_stop_book_tags_genre_write.sql) no write path files a
 * tag_type = 'genre' row in book_tags, so a book added after it has no genre
 * there at all. Genre + subgenre together matches what these pages showed
 * before: between PR 2 and PR 5 the picker wrote both levels into book_tags
 * as 'genre' rows. A pending subgenre shows too -- "a pending subgenre is
 * fully working on the book. It shows on the book page"
 * (docs/genre-subgenre-build-spec.md:1575).
 *
 * legacyGenreNames is the book's old book_tags 'genre' names, shown ONLY
 * when the book has no book_genres and no book_subgenres row at all. Those
 * are books PR 5 left without one on purpose: Children's books, which have
 * no genre in the 20-genre canon, and the 18 undecided books from the
 * 36-book audit (docs/genre-subgenre-queries/pr5_preflight_check.sql lists
 * both groups). Neither can be backfilled, and without this the one genre
 * they did have would vanish from these two screens. Display only: nothing
 * that decides visibility, filtering or search reads it.
 */
export function bookGenreNames(book, legacyGenreNames = []) {
  const byId = (a, b) => (a?.id ?? 0) - (b?.id ?? 0);
  const genres    = [...(book?.book_genres    || [])].sort(byId).map(r => r?.genre?.name);
  const subgenres = [...(book?.book_subgenres || [])].sort(byId).map(r => r?.subgenre?.name);
  const current = [...new Set([...genres, ...subgenres].filter(Boolean))];
  if (current.length) return current;
  return [...new Set((legacyGenreNames || []).filter(Boolean))];
}

/** Standard Levenshtein edit distance, iterative, O(a·b) space. */
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array(b.length + 1);
  let curr = Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,      // deletion
        curr[j - 1] + 1,  // insertion
        prev[j - 1] + cost // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Flattens the fetched subgenres into one list to match against, resolving
 * every alias row to the canon row it stands for. "compare against canon
 * and the admin-maintained aliases" (Part 5) means an author's typed text
 * can land on an ALIAS's wording (e.g. "Queer") and should be offered the
 * CANON target it resolves to (LGBTQ+ Romance), not the alias's own name --
 * an alias row is not something to offer as if it were itself the answer.
 *
 * Only status === 'canon' rows are in the matching pool at all. A pending
 * write-in is deliberately invisible here: Part 5 is explicit that a
 * write-in "is not offered in the pickers... until it is promoted", and a
 * did-you-mean suggestion is exactly a picker offering it early.
 *
 * @param {Array<{id, genre_id, name, normalized_name, status, alias_of}>} subgenres
 * @param {Map<number,{id,name}>} genresById
 * @returns {Array<{id, genreId, genreName, name, normalizedName}>}
 *          one entry per CANON row (never an alias row itself) -- an alias
 *          row contributes its own normalizedName as a second way to reach
 *          the same target id/name.
 */
export function buildMatchIndex(subgenres, genresById) {
  const byId = new Map(subgenres.map(s => [s.id, s]));
  const entries = [];

  for (const row of subgenres) {
    if (row.status !== 'canon') continue;

    const target = row.alias_of ? byId.get(row.alias_of) : row;
    if (!target || target.status !== 'canon') continue; // an alias pointing at something missing/non-canon is not usable

    entries.push({
      id: target.id,
      genreId: target.genre_id,
      genreName: genresById.get(target.genre_id)?.name || '',
      targetName: target.name,
      // The text being compared against is THIS row's own wording (which,
      // for an alias row, is the loose spelling -- "Queer", "Sapphic" --
      // not the canon name), so "Queer" can be found even though the
      // canon row it resolves to is spelled "LGBTQ+ Romance".
      matchedOn: row.name,
      normalized: row.normalized_name || normalizeSubgenreName(row.name),
    });
  }

  return entries;
}

// Only a near-identical whole string is worth flagging as "maybe a typo".
// Scaled to length so a 4-character name (Noir) isn't held to the same
// absolute distance as a 20-character one, and small enough that two
// genuinely different subgenres of similar length don't collide (verified
// against the spec's own bad-match case, "Bromance" vs "MM Romance", below).
function editDistanceThreshold(len) {
  if (len <= 8) return 1;
  if (len <= 16) return 2;
  return 3;
}

/**
 * Find what a typed subgenre name might mean, given which genre is
 * currently selected. Returns one of:
 *
 *   { kind: 'exact-same-genre',  target }  -- silently attach, no prompt.
 *       The typed text IS the canon spelling (or an alias's spelling) of a
 *       subgenre that already belongs to the genre the author has open.
 *       There is nothing to ask -- picking it from a list or typing its
 *       exact name are the same action. This is what fixes the "LGBTQ+
 *       Romance saved as a write-in instead of recognised" defect.
 *
 *   { kind: 'exact-other-genre', target }  -- show the prompt.
 *       Same exact-text case, but the match lives under a DIFFERENT genre
 *       than the one open right now (the "Romantasy typed under Romance"
 *       case the spec calls out by name). Still needs a prompt, because
 *       accepting it is not just picking a subgenre -- book_subgenres has
 *       no way to file Fantasy's "Romantasy" under Romance, so accepting it
 *       also adds Fantasy as a genre on the book. That side effect needs
 *       the author's yes, not a silent add.
 *
 *   { kind: 'near', target }  -- show the prompt, ordinary case.
 *       Not the same text, but close enough it might be what they meant --
 *       either a whole-word prefix (Sapphic -> Sapphic Romance, Noir ->
 *       Noir & Hardboiled) or a short edit distance on similarly-long
 *       strings (Cosy Fantasy -> Cozy Fantasy). Same-genre matches are
 *       preferred over other-genre ones when both exist.
 *
 *   null -- nothing close enough. Falls through to the write-in row.
 *
 * @param {string} typed
 * @param {number} selectedGenreId
 * @param {Array} matchIndex  from buildMatchIndex()
 */
export function findSubgenreMatch(typed, selectedGenreId, matchIndex) {
  const norm = normalizeSubgenreName(typed);
  if (!norm) return null;

  let exactSame = null, exactOther = null;
  const near = []; // { entry, sameGenre, distance }

  for (const entry of matchIndex) {
    const sameGenre = entry.genreId === selectedGenreId;

    if (entry.normalized === norm) {
      if (sameGenre && !exactSame) exactSame = entry;
      else if (!sameGenre && !exactOther) exactOther = entry;
      continue; // an exact hit is never also a "near" candidate
    }

    // Whole-word prefix: typed is the candidate's leading word(s), not just
    // any substring anywhere in it. This is what tells "Sapphic" apart from
    // "Horror" against "Cosmic & Eldritch Horror" -- Horror is the LAST
    // word there, not the first, so it does not qualify. Deliberately not
    // n.includes(t) (Part 5's defect 2): that matched Horror against
    // Cosmic & Eldritch Horror too, because includes() does not care where
    // the substring falls.
    if (entry.normalized.startsWith(norm + ' ')) {
      near.push({ entry, sameGenre, distance: 0 });
      continue;
    }

    // Near-identical whole string, length-scaled, and only when the two
    // strings are close to the same length -- guards against exactly the
    // false positive defect 2 also produced (a short typed word scoring
    // "close enough" to a long, unrelated candidate purely because the long
    // one contains similar letters).
    const lenRatio = Math.min(norm.length, entry.normalized.length) /
                      Math.max(norm.length, entry.normalized.length);
    if (lenRatio >= 0.6) {
      const dist = levenshtein(norm, entry.normalized);
      if (dist <= editDistanceThreshold(Math.min(norm.length, entry.normalized.length))) {
        near.push({ entry, sameGenre, distance: dist });
      }
    }
  }

  if (exactSame) return { kind: 'exact-same-genre', target: exactSame };
  if (exactOther) return { kind: 'exact-other-genre', target: exactOther };

  if (near.length) {
    near.sort((a, b) => (a.sameGenre === b.sameGenre ? a.distance - b.distance : a.sameGenre ? -1 : 1));
    return { kind: 'near', target: near[0].entry };
  }

  return null;
}

/**
 * The three-way disambiguation "new doc for genre" requires for removing a
 * genre or subgenre chip (PR 2 section, the table under "So the empty-result
 * branch is not an error branch here"). Pure and table-agnostic: the caller
 * does the actual delete-then-maybe-recheck against book_genres or
 * book_subgenres and hands the results in here, the same shape either way.
 *
 * The caller's required sequence, mirroring author #90's fix:
 *   1. const { data: deletedRows } = await supabase.from(table).delete()
 *        .eq('book_id', bookId).eq(idCol, id).eq('source', 'author')
 *        .select('id')
 *   2. If deletedRows is empty, a second read to tell the two zero-row
 *      cases apart:
 *        const { data: remaining } = await supabase.from(table).select('id')
 *          .eq('book_id', bookId).eq(idCol, id).limit(1)
 *      stillExists = (remaining?.length > 0)
 *
 * @param {{deletedRows: Array, stillExists: boolean}} outcome
 * @returns {{removed: boolean, reason: 'removed'|'reader-credited'|'failed', message: string|null}}
 */
export function interpretDeleteOutcome({ deletedRows, stillExists }) {
  if (deletedRows && deletedRows.length > 0) {
    return { removed: true, reason: 'removed', message: null };
  }
  if (stillExists) {
    // Not a failure. source has no key role in UNIQUE (book_id, genre_id) --
    // a reader's row for the same genre got there first, the author's
    // delete correctly matched zero rows (it is scoped to source='author'),
    // and the genre is still true of the book. See "One limitation to build
    // against rather than around" in the doc: this does not mean "you AND
    // 3 readers picked this" -- it only knows one row, credited to whoever
    // got there first, so the message must not claim to know more than that.
    return { removed: false, reason: 'reader-credited', message: 'A reader added this, it stays on the book.' };
  }
  // No row was deleted and none remains either. Genuinely refused (not
  // this author's book, RLS down, network error, etc.) -- author #90's
  // exact failure mode, just on a different table.
  return { removed: false, reason: 'failed', message: "Couldn't remove this, try again." };
}

/**
 * What to insert and what to remove when an author submits their genre or
 * subgenre picks, given what already exists on the book. Same table shape,
 * same function, for book_genres and book_subgenres both -- pass genre ids
 * against book_genres rows, subgenre ids against book_subgenres rows.
 *
 * Mirrors splitAgainstExisting in src/lib/tagRows.js, but simpler: a genre
 * has no "bucket" to be re-filed into the way a trope does between tag_type
 * groups, so there is no toRebucket case here -- an entity is either on the
 * book or it is not.
 *
 * AddBook.jsx does not touch the database until Submit (see the formDraft
 * comment above DesktopAddBook's state), so this is a bulk reconciliation
 * run once at save time, the same architecture updateBook already uses for
 * book_tags: delete every author-sourced row the current picks no longer
 * want, then insert whatever is newly wanted and not already held by
 * someone else. THE BUG THIS PREVENTS: before PR2's delete-policy migration,
 * book_genres/book_subgenres had admin-only delete, so the bulk delete below
 * would have matched zero rows under RLS, the stale row would still be
 * there when toInsert is computed against "existing", and an unchecked
 * genre would reappear after every save -- "the author sees the genre
 * disappear and finds it back on reload" (the doc's own description of this
 * exact failure shape).
 *
 * @param {number[]} desiredIds  what the author's picker currently holds
 * @param {Array<{entityId:number, source:string}>} existingRows
 *        every row on the book for this entity type, any source
 * @returns {{
 *   toInsert: number[],
 *   toRemoveAuthorIds: number[],
 *   keptOtherSource: number[],
 * }}
 *   toInsert           -- ids to insert as source='author'. Already-held ids
 *                         (any source) are excluded -- inserting again would
 *                         only hit UNIQUE (book_id, genre_id) or re-credit a
 *                         row that is not the author's to re-credit.
 *   toRemoveAuthorIds  -- author-sourced ids the picker no longer holds.
 *                         Safe to bulk-delete with source='author' in the
 *                         WHERE clause.
 *   keptOtherSource    -- ids the author's picker no longer holds, but that
 *                         are NOT the author's to remove (source is reader
 *                         or api). These render as pre-checked chips from
 *                         initialBook regardless of source (matching how
 *                         book_tags chips already seed), so unchecking one
 *                         of these is exactly the case "new doc for genre"
 *                         requires a message for: it will still be there
 *                         after save, and the form has to say so rather than
 *                         let the chip just disappear and come back.
 */
export function diffGenrePicks(desiredIds, existingRows) {
  const desired = new Set(desiredIds);
  const alreadyHeld = new Set((existingRows || []).map(r => r.entityId));

  // esomebi review (PR2): desiredIds is not de-duplicated by anything upstream
  // of here. [5, 5] used to produce toInsert: [5, 5], and since both
  // book_genres and book_subgenres are UNIQUE on (book_id, entity_id), a
  // multi-row .insert() with the same id twice aborts the whole statement --
  // every genre/subgenre on the save lost, not just the duplicate. The
  // de-duplication has to live here rather than trust the picker to never
  // produce a repeat, same reasoning as splitTagsForInsert (src/lib/tagRows.js).
  // `desired` (the Set built above) is already the de-duplicated id list.
  const toInsert = [...desired].filter(id => !alreadyHeld.has(id));

  const toRemoveAuthorIds = [];
  const keptOtherSource = [];
  for (const row of existingRows || []) {
    if (desired.has(row.entityId)) continue; // still wanted -- leave exactly as is
    if (row.source === 'author') toRemoveAuthorIds.push(row.entityId);
    else keptOtherSource.push(row.entityId);
  }

  return { toInsert, toRemoveAuthorIds, keptOtherSource };
}
